import { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import classnames from 'classnames';
import { IconButton, Icon, Typography } from '@getsynapse/design-system';
import { getDateTime, oldGetDateTime } from './helpers';
import intl from 'react-intl-universal';
import {
  ProjectResource,
  FacilitatorResource,
} from 'utils/types/learningSchedule';
import { generateReadableRecurrancePattern } from '../../helpers/helpers';
import { hexToRgba } from 'Pages/helpers';
import 'Pages/LearningSchedule/components/Events/styles.css';
import eventTypeIcon from 'assets/icons/Paste.svg';
import projectListIcon from 'assets/icons/project-list-quick-info.svg';
import teamIcon from 'assets/icons/Team.svg';
import worldIcon from 'assets/icons/Multilingual.svg';
import dueDateIcon from 'assets/icons/due-date.svg';
import { renderProjectNameLink } from './helpers';
import { USER_STATUS } from 'utils/constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { userSummarySelectors } from 'state/normalized/userSummaries/userSummariesSlice';
import { DUMMY_PROJECT_ID } from 'state/Schedule/ScheduleSlice';
import {
  HOLIDAY_EVENT_BACKGROUND_COLOR,
  HOLIDAY_EVENT_COLOR,
} from 'Pages/LearningSchedule/constants/constants';
export const headerTemplate = (onClose: () => void) => {
  return () => (
    <div className='w-full h-8'>
      <IconButton
        name='close'
        onClick={onClose}
        className='float-right mt-2 mr-4 text-base text-neutral-dark'
      />
    </div>
  );
};

export interface EventProps {
  Id: string;
  Subject: string;
  IsAllDay: boolean;
  StartTime: Date;
  EndTime: Date;
  RecurrenceRule: string;
  EventType?: string;
  projects?: ProjectResource[];
  facilitators?: FacilitatorResource[];
  isOldEvent: boolean;
  createdBy?: string;
  eventColor: string;
  holidayCountry?: string;
}

export const ContentTemplate = (props: EventProps) => {
  const [isFacilitatorSectionOpen, setIsFacilitatorSectionOpen] =
    useState(false);
  const [isProjectsSectionOpen, setProjectsSectionOpen] = useState(false);
  const { eventTimezones } = useFlags();
  const {
    Subject,
    IsAllDay,
    StartTime,
    EndTime,
    RecurrenceRule,
    EventType,
    projects = [],
    facilitators = [],
    isOldEvent,
    createdBy,
    eventColor,
    holidayCountry,
  }: EventProps = props;

  const creator = useSelector((state: RootState) =>
    createdBy ? userSummarySelectors.selectById(state, createdBy) : undefined
  );

  const toggleFacilitatorSection = () => {
    setIsFacilitatorSectionOpen(!isFacilitatorSectionOpen);
  };

  const toggleProjectsSection = () => {
    setProjectsSectionOpen(!isProjectsSectionOpen);
  };

  const recurrenceLabel = generateReadableRecurrancePattern(RecurrenceRule);
  const isHolidayEvent = holidayCountry && !creator;

  const generateFacilitatorElements = (
    facilitators: FacilitatorResource[],
    dataTestId: string
  ) => {
    return facilitators.map((facilitator, index: number) => {
      return (
        <Typography
          key={index}
          variant='body2'
          data-testid={`${dataTestId}-${index}`}
          className='flex justify-between w-full'
        >
          {facilitator.status === USER_STATUS.REGISTERED_DISABLED ||
          facilitator.status === USER_STATUS.INVITED_DISABLED ? (
            <>
              <span>{facilitator.name}</span>
              <span>
                ({intl.get('USERS_PAGE.TABLE.USER_STATUS.DEACTIVATED')})
              </span>
            </>
          ) : (
            facilitator.name
          )}
        </Typography>
      );
    });
  };

  const generateProjectElements = (
    projects: ProjectResource[],
    dataTestId: string
  ) => {
    return projects.map((project: ProjectResource, index: number) => {
      return renderProjectNameLink({
        projectElement: project,
        index,
        dataTestId,
      });
    });
  };

  const toggleSectionElement = (
    isToggleSectionOpen: boolean,
    onClickHandler: () => void,
    dataTestId: string
  ) => {
    return (
      <div className='ml-auto' data-testid='quick-info__toggle'>
        <div
          onClick={onClickHandler}
          data-testid={dataTestId}
          className='flex'
          style={{
            cursor: 'pointer',
            transform: `${
              isToggleSectionOpen ? 'rotate(180deg)' : 'rotate(0deg)'
            }`,
            transition: 'all 0.2s linear',
          }}
        >
          <Icon name='chevron-down' className='text-lg text-neutral-dark' />
        </div>
      </div>
    );
  };

  const dummyProjectExists = (projects: ProjectResource[]) => {
    return projects.some((project) => project.id === DUMMY_PROJECT_ID);
  };

  return (
    <div className={classnames('w-full px-1 flex flex-col')}>
      <div
        className='px-4 py-2 flex flex-col border-l-6 mb-4 text-lg font-semibold text-primary-darker'
        style={{
          backgroundColor: holidayCountry
            ? HOLIDAY_EVENT_BACKGROUND_COLOR
            : hexToRgba(eventColor, 0.3),
          borderColor: holidayCountry ? HOLIDAY_EVENT_COLOR : eventColor,
        }}
      >
        <Typography variant='h6' className='content-center break-words'>
          {Subject}
        </Typography>

        {!isHolidayEvent && (
          <Typography variant='caption'>
            {intl.get('SCHEDULE.EVENTS.DETAILS_SECTION.CREATED_BY')}
            <span
              className={classnames({
                'text-primary-darker': !creator?.name,
              })}
            >
              {creator?.name ?? intl.get('FORMER_USER')}
            </span>
          </Typography>
        )}
      </div>
      <div className='flex flex-row pt-px'>
        <Icon
          src={dueDateIcon}
          className='w-6 h-6 pt-px pr-4 text-neutral-dark'
        />
        <div className='flex flex-col'>
          <Typography variant='body2'>
            {eventTimezones
              ? getDateTime(StartTime, EndTime, IsAllDay, isOldEvent)
              : oldGetDateTime(StartTime, EndTime, IsAllDay)}
          </Typography>
          <Typography variant='body2' data-cy='quick-view-recurrence-label'>
            {recurrenceLabel}
          </Typography>
        </div>
      </div>
      {isHolidayEvent && (
        <div
          className='flex flex-row pt-px'
          data-testid='holiday-country-label'
        >
          <Icon src={worldIcon} className='w-6 h-6 pt-px pr-4' />
          <Typography variant='body2'>{holidayCountry}</Typography>
        </div>
      )}
      {EventType && !isHolidayEvent && (
        <div className='flex flex-row pt-px'>
          <Icon src={eventTypeIcon} className='w-5 h-5 pt-px pr-4' />
          <Typography variant='body2'>
            {intl.get(
              `SCHEDULE.EVENTS.DETAILS_SECTION.EVENT_TYPES.${EventType?.toUpperCase()}`
            )}
          </Typography>
        </div>
      )}
      {facilitators && facilitators.length > 0 && !isHolidayEvent && (
        <div className='flex flex-row pt-px' data-testid='facilitators-list'>
          <Icon src={teamIcon} className='w-5 h-5 pt-px pr-4' />{' '}
          {facilitators.length > 4 ? (
            <div className='flex flex-col w-full'>
              <div className='flex'>
                <Typography variant='body2'>
                  {facilitators.length}{' '}
                  {intl.get('SCHEDULE.QUICK_VIEW.FACILITATORS')}
                </Typography>
                {toggleSectionElement(
                  isFacilitatorSectionOpen,
                  toggleFacilitatorSection,
                  'facilitators-toggle'
                )}
              </div>
              {isFacilitatorSectionOpen && (
                <div className='flex flex-col w-full'>
                  {generateFacilitatorElements(
                    facilitators,
                    'facilitators-list-elems'
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className='flex flex-col w-full'>
              {generateFacilitatorElements(facilitators, 'facilitators-list')}
            </div>
          )}
        </div>
      )}
      {projects.length > 0 && !dummyProjectExists(projects) && (
        <div className='flex flex-row pt-px' data-testid='projects-list'>
          <Icon src={projectListIcon} className='w-5 h-5 pt-px pr-4' />{' '}
          {projects.length > 4 ? (
            <div className='flex flex-col w-full'>
              <div className='flex'>
                <Typography variant='body2'>
                  {projects.length} {intl.get('SCHEDULE.QUICK_VIEW.PROJECTS')}
                </Typography>
                {toggleSectionElement(
                  isProjectsSectionOpen,
                  toggleProjectsSection,
                  'projects-toggle'
                )}
              </div>
              {isProjectsSectionOpen && (
                <div className='flex flex-col flex-1'>
                  {renderProjectNameLink({
                    projectElement: projects[0],
                    index: 0,
                    dataTestId: 'projects-list',
                  })}
                  {generateProjectElements(
                    projects.slice(1),
                    'projects-list-elems'
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className='flex flex-col flex-1' key={projects[0].id}>
              {generateProjectElements(projects, 'projects-list')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContentTemplate;
